import { create } from "zustand";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { IdentityVerificationId } from "../../types";

export type CustomerCredentials = {
  identityId: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
};

export type BookingDetails = {
  bookingId: string;
  status: string;
  agentId?: string;
  advisorName?: string;
  advisorEmail?: string;
  startsAt?: Date;
  endAt?: Date;
  externalId?: string;
};

export type PoaDetails = {
  poaId: string | null;
  validTo: string | null;
  isValid?: boolean;
};

export type CustomerData = {
  phoneNumber: string;
  email: string;
  personalNumber: string;
  transactionId: string;
  validFrom: string;
  validTo: string | null;
  name: string;
  firstName: string;
  lastName: string;
  identityVerificationId: IdentityVerificationId;
  credentials: CustomerCredentials;
  booking: BookingDetails;
  poa: PoaDetails;
  hasLockedAdvice: boolean;
};

export type CustomerDataStore = {
  customerData: CustomerData;
  updateEmail: (email: string) => void;
  updatePhoneNumber: (phoneNumber: string) => void;
  updatePersonalNumber: (personalNumber: string) => void;
  updateName: (firstName: string, lastName: string) => void;
  updateTransactionId: (transactionId: string) => void;
  updateValidFrom: (date: string) => void;
  updateValidTo: (date: string | null) => void;
  updateIdentityVerificationId: (id: string | null) => void;
  updateCredentials: (credentials: CustomerCredentials) => void;
  updateBooking: (booking: BookingDetails) => void;
  updatePoa: (poa: PoaDetails) => void;
  setCustomerData: (data: Partial<CustomerData>) => void;
  updateHasLockedAdvice: (hasLockedAdvice: boolean) => void;
  resetCustomerData: () => void;
};

export const getStoredCredentials = (): CustomerCredentials => {
  const stored = sessionStorage.getItem("customerData");
  return stored ? JSON.parse(stored) : ({} as CustomerCredentials);
};

type ContactDetails = {
  email: string;
  phoneNumber: string;
};

const CONTACT_DETAILS_KEY = "contactDetails";
const POA_DETAILS_KEY = "poaDetails";
const VERIFICATION_DATA_KEY = "verificationData";

type VerificationData = {
  identityVerificationId: IdentityVerificationId;
  transactionId: string;
  personalNumber: string;
};

const emptyVerificationData = (): VerificationData => ({
  identityVerificationId: null,
  transactionId: "",
  personalNumber: "",
});

const getStoredVerificationData = (): VerificationData => {
  const stored = sessionStorage.getItem(VERIFICATION_DATA_KEY);
  return stored
    ? { ...emptyVerificationData(), ...JSON.parse(stored) }
    : emptyVerificationData();
};

const saveVerificationData = (data: VerificationData): void => {
  sessionStorage.setItem(VERIFICATION_DATA_KEY, JSON.stringify(data));
};

const clearVerificationData = (): void => {
  sessionStorage.removeItem(VERIFICATION_DATA_KEY);
};

const getStoredContactDetails = (): ContactDetails => {
  const stored = sessionStorage.getItem(CONTACT_DETAILS_KEY);
  return stored ? JSON.parse(stored) : { email: "", phoneNumber: "" };
};

const saveContactDetails = (contactDetails: ContactDetails): void => {
  sessionStorage.setItem(CONTACT_DETAILS_KEY, JSON.stringify(contactDetails));
};

const getStoredPoaDetails = (): PoaDetails => {
  const stored = sessionStorage.getItem(POA_DETAILS_KEY);
  return stored
    ? JSON.parse(stored)
    : { poaId: null, validTo: null, isValid: undefined };
};

const savePoaDetails = (poa: PoaDetails): void => {
  sessionStorage.setItem(POA_DETAILS_KEY, JSON.stringify(poa));
};

export const isFullCredentials = (
  credentials?: CustomerCredentials,
): boolean => {
  return !!(credentials?.accessKeyId && credentials?.secretAccessKey);
};

const getInitialCustomerData = (): CustomerData => ({
  phoneNumber: getStoredContactDetails().phoneNumber,
  email: getStoredContactDetails().email,
  personalNumber: "",
  transactionId: getStoredVerificationData().transactionId,
  validFrom: "",
  validTo: null,
  name: "",
  firstName: "",
  lastName: "",
  identityVerificationId: getStoredVerificationData().identityVerificationId,
  credentials: getStoredCredentials(),
  booking: {
    bookingId: "",
    status: "",
  },
  poa: getStoredPoaDetails(),
  hasLockedAdvice: false,
});

export const useUpdateCustomerData = create<CustomerDataStore>((set) => ({
  customerData: getInitialCustomerData(),
  updateEmail: (email) =>
    set((state) => {
      const contactDetails = getStoredContactDetails();
      saveContactDetails({ ...contactDetails, email });
      return {
        customerData: { ...state.customerData, email: email },
      };
    }),
  updatePhoneNumber: (phoneNumber) =>
    set((state) => {
      const contactDetails = getStoredContactDetails();
      saveContactDetails({ ...contactDetails, phoneNumber });
      return {
        customerData: { ...state.customerData, phoneNumber: phoneNumber },
      };
    }),
  updateName: (firstName, lastName) =>
    set((state) => ({
      customerData: {
        ...state.customerData,
        name: firstName + " " + lastName,
        firstName: capitalizeFirstLetter(firstName),
        lastName: capitalizeFirstLetter(lastName),
      },
    })),
  updatePersonalNumber: (personalNumber) =>
    set((state) => {
      const stored = getStoredVerificationData();
      const isDifferentPerson =
        stored.personalNumber !== "" &&
        stored.personalNumber !== personalNumber;
      if (isDifferentPerson) {
        clearVerificationData();
      }
      return {
        customerData: {
          ...state.customerData,
          personalNumber: personalNumber,
          ...(isDifferentPerson && {
            identityVerificationId: null,
            transactionId: "",
          }),
        },
      };
    }),
  updateTransactionId: (transactionId) =>
    set((state) => {
      saveVerificationData({
        ...getStoredVerificationData(),
        transactionId,
        personalNumber: state.customerData.personalNumber,
      });
      return {
        customerData: {
          ...state.customerData,
          transactionId: transactionId,
        },
      };
    }),
  updateIdentityVerificationId: (id) =>
    set((state) => {
      const resolved = id ?? state.customerData.identityVerificationId;
      saveVerificationData({
        ...getStoredVerificationData(),
        identityVerificationId: resolved,
        personalNumber: state.customerData.personalNumber,
      });
      return {
        customerData: {
          ...state.customerData,
          identityVerificationId: resolved,
        },
      };
    }),
  updateValidFrom: (date) =>
    set((state) => ({
      customerData: { ...state.customerData, validFrom: date },
    })),
  updateValidTo: (date) =>
    set((state) => ({
      customerData: { ...state.customerData, validTo: date },
    })),
  updateCredentials: (credentials: CustomerCredentials) => {
    return set((state) => {
      const updatedData = {
        ...state.customerData,
        credentials: credentials,
      };

      sessionStorage.setItem(
        "customerData",
        JSON.stringify(updatedData.credentials),
      );
      return { customerData: updatedData };
    });
  },
  updateBooking: (booking: BookingDetails) =>
    set((state) => ({
      customerData: { ...state.customerData, booking },
    })),
  updatePoa: (poa: PoaDetails) =>
    set((state) => {
      savePoaDetails(poa);
      return {
        customerData: { ...state.customerData, poa },
      };
    }),
  updateHasLockedAdvice: (hasLockedAdvice: boolean) =>
    set((state) => ({
      customerData: { ...state.customerData, hasLockedAdvice },
    })),
  setCustomerData: (data: Partial<CustomerData>) =>
    set((state) => {
      if (data.poa) {
        savePoaDetails(data.poa);
      }
      return {
        customerData: { ...state.customerData, ...data },
      };
    }),
  resetCustomerData: () =>
    set(() => {
      sessionStorage.removeItem(CONTACT_DETAILS_KEY);
      sessionStorage.removeItem(POA_DETAILS_KEY);
      sessionStorage.removeItem(VERIFICATION_DATA_KEY);
      sessionStorage.removeItem("customerData");
      return {
        customerData: getInitialCustomerData(),
      };
    }),
}));
